import React from "react";
import {
  Box,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  Stack,
  Typography,
} from "@mui/material";
import {
  CheckCircle,
  Error,
  FileCopy,
  Inventory2,
  Schedule,
  Storage,
} from "@mui/icons-material";
import { useQuery } from "react-query";
import { getCognitoAiImageReport } from "../../services/CognitoAi";

const CognitoAiStats = () => {
  const {
    data: {
      data: {
        totalUploads = 0,
        totalBytes = 0,
        completedUploads = 0,
        startedUploads = 0,
        inProgressUploads = 0,
        failedUploads = 0,
      } = {},
    } = {},
  } = useQuery("getCognitoAiImageReport", getCognitoAiImageReport, {
    refetchOnWindowFocus: false,
    useErrorBoundary: false,
    suspense: true,
  });

  const formatDataSize = (bytes) => {
    if (typeof bytes === "string") {
      return bytes;
    }
    if (!bytes || Number.isNaN(Number(bytes))) {
      return "0 MB";
    }
    return `${(Number(bytes) / (1024 * 1024)).toFixed(2)} MB`;
  };

  const completionPercent =
    totalUploads > 0 ? Math.round((completedUploads / totalUploads) * 100) : 0;
  const activeUploads = Number(startedUploads) + Number(inProgressUploads);

  const summaryCards = [
    {
      title: "Total Uploads",
      value: totalUploads,
      icon: <Inventory2 fontSize="large" />,
      color: "#1976d2",
    },
    {
      title: "Total Bytes",
      value: formatDataSize(totalBytes),
      icon: <Storage fontSize="large" />,
      color: "#1976d2",
    },
  ];

  const statusCards = [
    {
      title: "Completed",
      value: completedUploads,
      icon: <CheckCircle fontSize="large" />,
      color: "#2e7d32",
    },
    {
      title: "Started Uploads",
      value: startedUploads,
      icon: <FileCopy fontSize="large" />,
      color: "#1976d2",
    },
    {
      title: "In Progress",
      value: inProgressUploads,
      icon: <Schedule fontSize="large" />,
      color: "#ed6c02",
    },
    {
      title: "Failed",
      value: failedUploads,
      icon: <Error fontSize="large" />,
      color: "#d32f2f",
    },
  ];

  return (
    <Box sx={{ mb: 3 }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "flex-start", sm: "center" }}
        justifyContent="space-between"
        spacing={1.5}
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="h6" fontWeight={700}>
            Upload Overview
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Real-time status of Cognito AI image uploads
          </Typography>
        </Box>
        <Chip
          color={
            completionPercent === 100 && totalUploads > 0
              ? "success"
              : "default"
          }
          label={`Completion ${completionPercent}%`}
          variant="outlined"
        />
      </Stack>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          gap: 2,
        }}
      >
        {summaryCards.map((stat) => (
          <Card variant="outlined"
            key={stat.title}
            elevation={0}
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              background:
                "linear-gradient(180deg, rgba(25, 118, 210, 0.04) 0%, rgba(255, 255, 255, 1) 40%)",
            }}
          >
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Box
                  sx={{
                    color: stat.color,
                    bgcolor: `${stat.color}1A`,
                    borderRadius: 2,
                    p: 1,
                    display: "inline-flex",
                  }}
                >
                  {stat.icon}
                </Box>
                <Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 0.5 }}
                  >
                    {stat.title}
                  </Typography>
                  <Typography variant="h5" component="div" fontWeight={700}>
                    {stat.value}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        ))}
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "repeat(1, minmax(0, 1fr))",
            sm: "repeat(2, minmax(0, 1fr))",
            md: "repeat(4, minmax(0, 1fr))",
          },
          gap: 2,
          mt: 2,
        }}
      >
        {statusCards.map((stat) => (
          <Card variant="outlined"
            key={stat.title}
            elevation={0}
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
            }}
          >
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Box
                  sx={{
                    color: stat.color,
                    bgcolor: `${stat.color}1A`,
                    borderRadius: 2,
                    p: 1,
                    display: "inline-flex",
                  }}
                >
                  {stat.icon}
                </Box>
                <Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 0.5 }}
                  >
                    {stat.title}
                  </Typography>
                  <Typography variant="h5" component="div" fontWeight={700}>
                    {stat.value}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        ))}
      </Box>

      <Card variant="outlined"
        elevation={0}
        sx={{
          mt: 2,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
        }}
      >
        <CardContent>
          <Stack
            direction={{ xs: "column", md: "row" }}
            alignItems={{ xs: "flex-start", md: "center" }}
            justifyContent="space-between"
            spacing={1.5}
            sx={{ mb: 1 }}
          >
            <Typography variant="subtitle1" fontWeight={600}>
              Upload Health
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Chip
                label={`Active ${activeUploads}`}
                color="warning"
                size="small"
              />
              <Chip
                label={`Failed ${failedUploads}`}
                color="error"
                size="small"
              />
            </Stack>
          </Stack>
          <LinearProgress
            variant="determinate"
            value={completionPercent}
            sx={{ height: 8, borderRadius: 999 }}
          />
        </CardContent>
      </Card>
    </Box>
  );
};

export default CognitoAiStats;
